import { Flex, Text, Box, Dialog, Portal, CloseButton } from "@chakra-ui/react";
import { Button, Link } from "@digital-services-gard-as/anchor-react";
import { InfoCircleIcon } from "@digital-services-gard-as/anchor-icons";
import { useQuery } from "@tanstack/react-query";
import { getSubclaimHandlerEmailQueryOptions } from "@/api/queryOptions/claimsApiQueryOptions";

import { useTranslation } from "react-i18next";
import { SyntheticEvent, useCallback, useState } from "react";

type TargetedClaim = {
    rejectedInvoiceMessage: string;
    claimsRef: string;
    subClaimRef?: string;
};

type RejectedInvoiceModalProps = {
    targetedClaim: TargetedClaim;
};

type RejectedClaimsHandlerProps = {
    message: string;
};

export const RejectedInvoiceModal = ({ targetedClaim }: RejectedInvoiceModalProps) => {
    const { t } = useTranslation(["invoices", "main"]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const { data: claimsHandlerEmailResult } = useQuery({
        ...getSubclaimHandlerEmailQueryOptions(targetedClaim?.claimsRef, targetedClaim?.subClaimRef),
        enabled: isDialogOpen
    });
    const claimsHandlerEmail = claimsHandlerEmailResult?.email ?? "";
    const hasClaimsHandlerEmail = claimsHandlerEmail.trim().length > 0;
    const rejectedInvoiceMessage = targetedClaim?.rejectedInvoiceMessage ?? "";

    const handleStopPropagation = useCallback((e: SyntheticEvent) => {
        e.stopPropagation();
    }, []);

    const handleOpenChange = useCallback((e: { open: boolean }) => {
        setIsDialogOpen(e.open);
    }, []);

    return (
        <Dialog.Root open={isDialogOpen} onOpenChange={handleOpenChange}>
            <Dialog.Trigger asChild>
                <Button
                    data-testid="invoiceRejectedBtn"
                    variant="secondary"
                    icon={<InfoCircleIcon />}
                    size="sm"
                    aria-label={t("rejectedInvoice.ariaLabel")}
                    onClick={handleStopPropagation}
                    onKeyUp={handleStopPropagation}
                    style={{ fontSize: "1.2rem", padding: "0.5rem" }}
                />
            </Dialog.Trigger>
            <Portal>
                <Dialog.Backdrop p="1rem" />
                <Dialog.Positioner>
                    <Dialog.Content p="1rem" data-testid="rejectedInvoiceModal">
                        <Dialog.Header textAlign="left">
                            <Dialog.Title>{t("invoices:rejectedInvoice.title")}</Dialog.Title>
                        </Dialog.Header>
                        <Dialog.CloseTrigger asChild id="rejectedInvoiceModalCloseBtn">
                            <CloseButton size="sm" />
                        </Dialog.CloseTrigger>
                        <Dialog.Body>
                            <Flex gap={5} flexDir="column">
                                <Flex>
                                    <DisplayRejectedInvoice message={rejectedInvoiceMessage} />
                                </Flex>
                                <Flex>{t("invoices:rejectedInvoice.claimsHandler")}</Flex>
                                <Flex mb="1rem">
                                    {hasClaimsHandlerEmail ? (
                                        <Link href={"mailto:" + claimsHandlerEmail}>
                                            <Text asChild>
                                                <u>{claimsHandlerEmail}</u>
                                            </Text>
                                        </Link>
                                    ) : (
                                        <Text></Text>
                                    )}
                                </Flex>
                            </Flex>
                            <Flex gap="1rem" mt="1rem" w="100%" justifyContent="flex-end">
                                <Dialog.ActionTrigger asChild>
                                    <Button data-testid="cancelBtn" variant="secondary" onClick={handleStopPropagation}>
                                        {t("main:close")}
                                    </Button>
                                </Dialog.ActionTrigger>
                            </Flex>
                        </Dialog.Body>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

const RejectedClaimsHandler = ({ message }: RejectedClaimsHandlerProps) => {
    const { t } = useTranslation("invoices");
    return (
        <Flex flexDir={"column"}>
            <Flex>
                <Text fontSize="13px" asChild>
                    <b>{t("rejectedInvoice.claimsHandlerMessage")}</b>
                </Text>
            </Flex>
            <Box bgColor={"anchor.brown.200"} borderRadius="5px" p="1rem">
                <Text asChild>
                    <em>{message}</em>
                </Text>
            </Box>
        </Flex>
    );
};

const RejectedInvoiceDefault = () => {
    const { t } = useTranslation("invoices");
    return (
        <Flex>
            <Text>{t("rejectedInvoice.defaultMessage")}</Text>
        </Flex>
    );
};

const DisplayRejectedInvoice = ({ message }: RejectedClaimsHandlerProps) => {
    if (message === "") {
        return <RejectedInvoiceDefault />;
    } else {
        return <RejectedClaimsHandler message={message} />;
    }
};
